import dayjs from "dayjs";
import { html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ScheduleService } from "../../api";
import AirDatepicker from 'air-datepicker';
import { repeat } from "lit/directives/repeat.js";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { ProductSelectionContext, productSelectionContextKey } from "../product-selection.context";
import { consume } from "@lit/context";
import { when } from "lit/directives/when.js";
import localeEn from 'air-datepicker/locale/en';

//import { Datepicker } from 'vanillajs-datepicker';

@customElement("date-picker")
export class DatePickerComponent extends LitElement {
    private picker : AirDatepicker<HTMLElement> | null = null
    //private picker : Datepicker | null = null;
    private datePickerAnchor : Ref<HTMLElement> = createRef();
    private datePickerInput: Ref<HTMLInputElement> = createRef();

    @state()
    private selectedDate: dayjs.Dayjs | null = null;

    @state()
    private quickDates: dayjs.Dayjs[] = [];

    @consume({ context: productSelectionContextKey })
    private productSelectionContext! : ProductSelectionContext;

    constructor() {
        super();
    }

    override async connectedCallback() {
        super.connectedCallback();

        this.productSelectionContext.selectedDate$.subscribe({ next: this.handleSelectedDateChanged.bind(this) });

        const { data, error } = await ScheduleService.getLastBookableDate();

        if (error || !data) {
            console.error("Error fetching last bookable date", error);
            return;
        }

        const firstAvailableDate = data.firstDate ? dayjs(new Date(data.firstDate)) : dayjs();
        const lastBookableDate = data.lastDate ? dayjs(new Date(data.lastDate)): dayjs();

        let shortcutDate = firstAvailableDate;
        let lastShortcutDate = shortcutDate.add(3, "day");

        if (lastShortcutDate.isAfter(lastBookableDate)) {
            lastShortcutDate = lastBookableDate;
        }

        while (shortcutDate.isBefore(lastShortcutDate) || shortcutDate.isSame(lastShortcutDate, "day")) {
            this.quickDates.push(shortcutDate);
            shortcutDate = shortcutDate.add(1, "day");
        }

        this.selectedDate = this.quickDates[0];

        const datePickerInputElement = this.datePickerInput.value;
        const datePickerPositionElement = this.datePickerAnchor.value;

        // this.picker = flatpickr(datePickerInputElement as HTMLElement, {
        //     positionElement: datePickerPositionElement as HTMLElement,
        //     position: "below right",
        //     minDate: firstAvailableDate.toDate(),
        //     maxDate: lastBookableDate.toDate(),
        //     defaultDate: this.selectedDate.toDate(),
        //     clickOpens: false,
        //     onChange: (selectedDates: Date[]) => {
        //         if (selectedDates.length > 0) {
        //             this.selectDate(dayjs(selectedDates[0]));
        //         }
        //     }
        // });
        const clientPrefersTouch = window.matchMedia("(pointer: coarse)").matches;
        this.picker = new AirDatepicker(datePickerInputElement as HTMLElement, {
            locale: localeEn,
            firstDay: 1,
            isMobile: clientPrefersTouch,
            position: "bottom right",
            minDate: firstAvailableDate.toDate(),
            maxDate: lastBookableDate.toDate(),
            autoClose: true,
            fixedHeight: true,
            onSelect: ({ date }) => {
                const selectedDate = Array.isArray(date) ? date[0] : date;
                if (selectedDate) {
                    this.selectDate(dayjs(selectedDate));
                }
            },
        });

        // this.picker = new Datepicker(datePickerInputElement as HTMLElement, {
        //     autohide: true,
        //     minDate: firstAvailableDate.toDate(),
        //     maxDate: lastBookableDate.toDate(),
        //     defaultViewDate: this.selectedDate.toDate(),
        //     weekStart: 1,
        //     orientation: "bottom right",
        // });
        // datePickerInputElement?.addEventListener("change", (event: Event) => {
        //     const target = event.target as HTMLInputElement;
        //     if (target?.value) {
        //         this.selectDate(dayjs(target.value));
        //     }
        // });
    }

    protected override createRenderRoot() {
        return this;
    }
    protected override render() {
        const now = dayjs();
        const isQuickDateSelected = this.quickDates.findIndex(date => date.isSame(this.selectedDate, "day"));
        return html`
            <div class="bg-white rounded-lg p-4 flex flex-col">
                <div class="mb-4 text-2xl text-mcr-purple">
                    ${when(isQuickDateSelected === -1, () => html`${this.selectedDate?.format("ddd Do MMM")}`, () => html`Select a date`)} </div>
                <div class="grid grid-cols-[1fr_6rem] grid-rows-1 lg:grid-cols-[repeat(auto-fit,minmax(5rem,1fr))] gap-2 @container">
                    ${repeat(this.quickDates, date => date.toString(), date => html`
                        <button class="col-start-1 lg:col-start-auto block cursor-pointer rounded-lg transistion bg-mcr-purple lg:p-4 text-center text-white select-none hover:bg-mcr-purple-600 outline-offset-2 outline-mcr-purple ${this.selectedDate?.isSame(date, "day") ? "outline-2" : ""}"
                                @click=${() => { this.selectDate(date); }}>
                            <div class="inline lg:block text-base @xl:text-xl @3xl:text-3xl">${now.isSame(date, "day") ? "Today" : date.format("ddd")}</div>
                            <div class="inline lg:block">${now.isSame(date, "day") ? date.format("ddd Do MMM") : date.format("Do MMM")}</div>
                        </button>
                    `)}
                    <button class="col-start-2 row-start-1 row-span-4 lg:row-span-1 lg:col-start-auto block cursor-pointer rounded-lg transition bg-mcr-purple lg:p-4 text-center text-white select-none hover:bg-mcr-purple-600 outline-offset-2 outline-mcr-purple ${isQuickDateSelected === -1 ? "outline-2" : ""}"
                            @click=${(e: Event) => { e.preventDefault(); this.openDatePicker(); }}
                            ${ref(this.datePickerAnchor)}>
                        <div class="text-lg font-bold">
                            <svg class="mx-auto size-12">
                                <use href="#select-date" />
                            </svg>
                        </div>
                    </button>
                </div>
                <input ${ref(this.datePickerInput)} type="date" class="collapse h-0" />
            </div>
        `;
    }

    private selectDate(date: dayjs.Dayjs) {
        this.productSelectionContext.setSelectedDate(date);
    }

    private openDatePicker() {
        this.picker?.show();
    }

    private handleSelectedDateChanged(selectedDate: dayjs.Dayjs | null) {
        this.selectedDate = selectedDate;
        if(this.selectedDate && this.picker){
            //this.picker.setDate(this.selectedDate.toDate(), {  });
            this.picker.selectDate(this.selectedDate.toDate(), { silent: true });
        }
    }
}